import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { Network } from '@capacitor/network';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService } from '@core/services/alert/alert.service';
import { DaDataService } from '@core/services/dadata/da-data.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ApplicationOnCredit } from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';
import { StringUtilService } from '@core/services/utils/string-util.service';
import { TypeUtilService } from '@core/services/utils/type-util.service';
import { UtilsService } from '@core/services/utils/utils.service';

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { DaDataAddress } from '@shared/models/da-data.model';
import { FormFieldsListItem } from '@shared/models/modal-payment-form.model';
import { NetworkService } from '@shared/services/network.service';

import { Steps } from '../models/step.model';
import { CreateApplicationRequest } from '../store/store.model';
import { StoreService } from '../store/store.service';
import { PersonalDataFormService } from './personal-data-form.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-personal-data-step',
    templateUrl: 'personal-data-step.page.html',
    styleUrls: ['./personal-data-step.page.scss', '../new-credit.scss'],
    standalone: false
})
export class PersonalDataStepPage implements OnInit, OnDestroy {
  title: string = 'Персональные данные';
  subtitle: string = '';

  public loaded$: Observable<boolean>;

  public userFormGroup: FormGroup;
  public userFormFieldList: FormFieldsListItem[];
  public fillAdditionalPersonalData = false;
  public additionalDataFormGroup: FormGroup;
  public additionalDataFormFieldList: any;
  public updateMaritalFormFields = new Subject();
  public agreeRules = {
    title: 'Подтверждаю достоверность сведений, ознакомлен и согласен с условиями',
    text: `<h4>Согласен с тем, что:</h4>
    <p>1. Все сведения, содержащиеся в настоящем Заявлении-анкете, а также все затребованные Банком документы предоставлены исключительно для получения Кредита, однако Банк оставляет за собой право использовать их как доказательство при судебном разбирательстве.<br>
    2. Банк оставляет за собой право проверки любой сообщаемой заемщиком о себе информации.<br>
    3. Предоставление заемщиком о себе ложной и вводящей в заблуждение информации может повлечь за собой отказ в предоставлении Кредита, а также гражданскую и уголовную (ст. 176 УК РФ) ответственность.</p>`
  };
  public agreeInsurance = {
    title: 'Согласен на уступку прав требования третьим лицам',
    text: `<p>Даю свое согласие на то, что право требования по заключенному между мной и Банком Договору потребительского кредита может быть уступлено Банком третьим лицам (в т.ч. некредитным организациям).</p>`
  };
  public agreeDebt = {
    title: 'Согласен на взыскание в бесспорном порядке',
    text: `<p>Даю свое согласие на то, что в случае возникновения задолженности, Банк вправе производить взыскание по заключенному между мной и Банком Договору потребительского кредита в бесспорном порядке на основании исполнительной надписи нотариуса в соответствии с действующим законодательством РФ.</p>`
  };
  private timer;

  constructor(
    private onlineCreditService: OnlineCreditService,
    private stepManagerService: StepManagerService,
    private personalFormService: PersonalDataFormService,
    private store: StoreService,
    private route: ActivatedRoute,
    private addressService: DaDataService,
    private utilService: UtilsService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef,
    private navigationService: NavigationService,
    private networkService: NetworkService
  ) {
    this.loaded$ = this.stepManagerService.stepLoaded$;
  }

  get primaryButtonText() {
    return this.fillAdditionalPersonalData ? 'Далее' : 'Потдвердить';
  }

  get employerFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.employer as FormGroup;
  }

  get addressFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.address as FormGroup;
  }

  get prevDocFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.prevDoc as FormGroup;
  }

  get workFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.work as FormGroup;
  }

  get maritalFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.marital as FormGroup;
  }

  get rulesFormGroup(): FormGroup {
    return this.additionalDataFormGroup?.controls.rules as FormGroup;
  }

  async ngOnInit() {
    this.fillAdditionalPersonalData = TypeUtilService.isTruthy(
      this.route.snapshot.queryParams.fillAdditionalPersonalData
    );
    let action$ = this.personalFormService.getCheckPersonalDataForm().pipe(
      map((data) => {
        if (this.store.value.personalData && this.store.value.personalData.userFormGroup) {
          return {
            userFormGroup: this.store.value.personalData.userFormGroup,
            userFormFields: this.store.value.personalData.userFormField
          };
        }
        return {
          userFormGroup: data.form,
          userFormFields: data.fields
        };
      })
    );
    if (this.fillAdditionalPersonalData) {
      action$ = action$.pipe(
        switchMap((userFormCfg) =>
          this.personalFormService.getAdditionalPersonalDataForm().pipe(
            map((additionalForm) => {
              return {
                ...userFormCfg,
                additionalFormGroup: additionalForm.form,
                additionalFormField: additionalForm.fields
              };
            })
          )
        )
      );
    }
    action$.pipe(untilDestroyed(this)).subscribe(
      (res: any) => {
        this.userFormGroup = res.userFormGroup;
        this.userFormGroup.controls.snils.setValidators([Validators.required, this.snilsValidator.bind(this)]);
        this.userFormFieldList = !this.fillAdditionalPersonalData
          ? res.userFormFields
          : res.userFormFields.filter((field: FormFieldsListItem) => !field.onlyMainForm);
        if (res.additionalFormGroup) {
          this.additionalDataFormGroup = res.additionalFormGroup;
          const addressFormGroup = this.additionalDataFormGroup.controls.address as FormGroup;
          const employerFormGroup = this.additionalDataFormGroup.controls.employer as FormGroup;

          let actualAddress = addressFormGroup.controls.actualAddress.value;
          const reg = /\s?[а-яё0-9\/-]*\s?[0-9]+[а-я]*$/;

          while (!reg.test(actualAddress)) {
            actualAddress = actualAddress.slice(0, -1);
          }

          addressFormGroup.controls.actualAddress.setValue(actualAddress);

          addressFormGroup.controls.actualAddress.setValidators([
            Validators.required,
            this.addressValidator.bind(this)
          ]);
          employerFormGroup.controls.employersInn.setValidators([this.innValidator.bind(this)]);
          this.additionalDataFormFieldList = res.additionalFormField;
          const actualAddressControl = this.getActualAddressControl();
          this.fillAddressKladrAndUuid(actualAddressControl.value);
        }
        this.stepManagerService.setStepLoaded(true);
        this.subscribeOnChanges();
        const { snils, emailAddress, ...otherFields } = this.userFormGroup.getRawValue();
        let needVisitToBank = false;
        Object.keys(otherFields).forEach((field) => {
          if (!otherFields[field]) {
            needVisitToBank = true;
          }
        });
        if (needVisitToBank) {
          this.stepManagerService.showErrorStepAlert(
            'Для заполнения Заявки отсутствуют персональные данные. Для актуализации данных обратитесь в офис Банка'
          );
        }

        if (this.store.value.application.status === 'full_application') {
          this.stepManagerService.setStepLoaded(false);
          this.getProcessedBki();
        }
        // if (this.store.value.application.status === 'processed_bki') {
        //
        //   this.userFormGroup.updateValueAndValidity({ emitEvent: false });
        //   this.additionalDataFormGroup.updateValueAndValidity({ emitEvent: false });
        //   const formsIsValid = this.userFormGroup.valid && this.additionalDataFormGroup.valid;
        //   if (formsIsValid) {
        //     this.stepManagerService.setStepLoaded(false);
        //     this.store.updatePersonalDataForm({
        //       additionalFormGroup: this.additionalDataFormGroup,
        //       additionalFormField: this.additionalDataFormFieldList
        //     });
        //     const rulesFormGroup = this.additionalDataFormGroup.get("rules") as FormGroup;
        //     const isOnline = rulesFormGroup.get("agreeRules").value && rulesFormGroup.get("agreeInsurance").value
        //       && rulesFormGroup.get("agreeDebt").value;
        //     this.store.changeIsOnlineFlag(isOnline);
        //     this.store.updateApplication(this.getUpdatedData());
        //
        //     this.store.value.application = {
        //       ...this.store.value.application,
        //       actualAddress: !this.store.value.application.actualAddress ? this.store.value.application.address : this.store.value.application.actualAddress
        //     }
        //
        //   // this.stepManagerService.changeStep(Steps.applicationConfirmation);
        //     void this.router.navigate(['new-product/credit/application-confirmation'])
        //   }
        // }
      },
      (error) => {
        const message = error.errorText || (error.error && error.error.errorText);
        this.alertService.showInfoAlert(message || error);
        void this.navigationService.setRoot('/main');
      }
    );
  }

  ionViewWillEnter() {}

  ngOnDestroy() {}

  ionViewWillLeave() {}

  getProcessedBki() {
    this.processedBkiRequest();
    this.timer = setInterval(() => {
      this.getBkiData();
    }, 20000);
  }

  processedBkiRequest() {
    this.onlineCreditService
      .sendApplicationData(this.store.value.application)
      .pipe(untilDestroyed(this))
      .subscribe(
        (res) => {},
        (err) => {
          console.error(err);
          void this.navigationService.push('/new-product/credit/sign-agreements', { replaceUrl: true });
        }
      );
  }

  getBkiData() {
    this.onlineCreditService
      .getApplicationData()
      .pipe(untilDestroyed(this))
      .subscribe(
        (res) => {
          if (res.status === 'processed_bki') {
            this.store.updateApplication(res);
            clearInterval(this.timer);
            void this.navigationService.push('/new-product/credit/application-confirmation', { replaceUrl: true });
          }
        },
        (err) => {
          console.error(err);
          clearInterval(this.timer);
          void this.navigationService.push('/new-product/credit/sign-agreements', { replaceUrl: true });
        }
      );
  }

  continueProcess() {
    this.userFormGroup.updateValueAndValidity({ emitEvent: false });
    this.additionalDataFormGroup.updateValueAndValidity({ emitEvent: false });
    const formsIsValid = this.userFormGroup.valid && this.additionalDataFormGroup.valid;
    if (formsIsValid) {
      this.stepManagerService.setStepLoaded(false);
      this.store.updatePersonalDataForm({
        additionalFormGroup: this.additionalDataFormGroup,
        additionalFormField: this.additionalDataFormFieldList
      });
      const rulesFormGroup = this.additionalDataFormGroup.get('rules') as FormGroup;
      const isOnline =
        rulesFormGroup.get('agreeRules').value &&
        rulesFormGroup.get('agreeInsurance').value &&
        rulesFormGroup.get('agreeDebt').value;
      this.store.changeIsOnlineFlag(isOnline);
      this.store.updateApplication(this.getUpdatedData());

      // this.store.value.application = {
      //   ...this.store.value.application,
      //   actualAddress: !this.store.value.application.actualAddress ? this.store.value.application.address : this.store.value.application.actualAddress
      // }

      if (['full_application', 'consent'].includes(this.store.value.application.status)) {
        this.getProcessedBki();
      } else {
        this.onlineCreditService
          .sendApplicationData(this.store.value.application)
          .pipe(untilDestroyed(this))
          .subscribe(
            (res) => {
              this.store.updateApplication(res);
              isOnline
                ? void this.navigationService.push('/new-product/credit/application-confirmation', {
                    replaceUrl: true,
                    queryParams: { isFinalRequest: false }
                  })
                : this.stepManagerService.showOfflineAlert();
            },
            (error) => {
              const message = error.errorText || (error.error && error.error.errorText);
              this.alertService.showInfoAlert(message || error);
              this.stepManagerService.setStepLoaded(true);
            }
          );
      }
    } else {
      this.markAsTouchedForms('userFormGroup');
      this.markAsTouchedForms('additionalDataFormGroup', true);
    }
  }

  async handleOnPrimaryButtonClick() {
    await this.networkService.getStatus().then((status) => {
      if (!status) {
        this.alertService.showInfoAlert('Отсутствует подключение к интернету!');
        return;
      }
      this.fillAdditionalPersonalData ? this.continueProcess() : this.confirmPersonalData();
    });
  }

  confirmPersonalData() {
    this.userFormGroup.updateValueAndValidity({ emitEvent: false });
    if (this.userFormGroup.valid) {
      this.stepManagerService.setStepLoaded(false);
      this.store.updatePersonalDataForm({
        userFormGroup: this.userFormGroup,
        userFormField: this.userFormFieldList
      });

      const { isOnline, id } = this.store.value.application;
      this.onlineCreditService
        .sendApplicationData({
          snils: this.userFormGroup.get('snils').value,
          isOnline,
          id,
          emailAddress: this.userFormGroup.get('emailAddress').value
        })
        .pipe(untilDestroyed(this))
        .subscribe(
          (application: ApplicationOnCredit) => {
            this.store.updateApplication(application);
            this.navigationService.push('/new-product/credit/choice-office', { replaceUrl: true });
          },
          (error) => {
            const message = error.errorText || (error.error && error.error.errorText);
            this.alertService.showInfoAlert(message || error);
            this.stepManagerService.setStepLoaded(true);
          }
        );
    } else {
      this.markAsTouchedForms('userFormGroup');
    }
  }

  async personalDataChanged() {
    await this.networkService.getStatus().then((status) => {
      if (!status) {
        this.alertService.showInfoAlert('Отсутствует подключение к интернету!');
        return;
      }
      this.stepManagerService.setStepLoaded(false);
      this.store.changeIsOnlineFlag(false);
      void this.navigationService.push('/new-product/credit/choice-office', { replaceUrl: true });
    });
  }

  private getUpdatedData() {
    const addressForm = this.additionalDataFormGroup.get('address') as FormGroup;
    const employerForm = this.additionalDataFormGroup.get('employer') as FormGroup;
    const prevDocForm = this.additionalDataFormGroup.get('prevDock') as FormGroup;
    const maritalForm = this.additionalDataFormGroup.get('marital') as FormGroup;
    const rulesForm = this.additionalDataFormGroup.get('rules') as FormGroup;
    const workForm = this.additionalDataFormGroup.get('work') as FormGroup;
    return {
      ...addressForm.value,
      ...employerForm.value,
      ...prevDocForm.value,
      ...maritalForm.value,
      income: StringUtilService.removeSpaces(employerForm.controls.income.value),
      workExperience: workForm.controls.workExperienceYear.value * 12 + +workForm.controls.workExperienceMonth.value,
      spousesIncome: maritalForm.controls.spousesIncome.value || 'не имеет',
      agreeDebt: Number(rulesForm.get('agreeDebt').value),
      agreeRules: Number(rulesForm.get('agreeRules').value),
      agreeInsurance: Number(rulesForm.get('agreeInsurance').value)
    };
  }

  private subscribeOnChanges() {
    if (this.additionalDataFormGroup) {
      this.listenChangeAddressForm();
      this.listenChangePrevDocForm();
      this.listenChangeMaritalForm();
      this.listenAddressNotEqualChanges();
    }
  }

  private listenChangeAddressForm() {
    const addressForm = this.getActualAddressControl();
    const addressRequest$ = (address: string) => {
      return !address.length
        ? of([]).pipe(map(() => ({ suggestions: [], address })))
        : this.addressService.searchAddress(address).pipe(map((suggestions) => ({ suggestions, address })));
    };
    addressForm.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap((addressString) => addressRequest$(addressString)),
        untilDestroyed(this)
      )
      .subscribe(({ suggestions, address }) => {
        const addressField = this.additionalDataFormFieldList.address.find(
          (field) => field.controlName === 'actualAddress'
        );
        addressField.options = suggestions.map((a) => {
          return { value: a.value, caption: 'адрес: ' };
        });
        const foundedAddress = this.findAddress(address, suggestions);
        if (foundedAddress) {
          this.additionalDataFormGroup.get('address.actualAddressUuid').setValue(foundedAddress.data.fias_id);
          this.additionalDataFormGroup.get('address.kladrCod').setValue(foundedAddress.data.region_kladr_id);
        }
      });
  }

  private listenChangePrevDocForm() {
    this.additionalDataFormGroup
      .get('prevDock')
      .valueChanges.pipe(startWith(this.additionalDataFormGroup.get('prevDock').value), untilDestroyed(this))
      .subscribe((res) => {
        this.checkPrevDock(res);
      });
  }

  private listenChangeMaritalForm() {
    const maritalFormGroup = this.additionalDataFormGroup.controls.marital as FormGroup;
    maritalFormGroup
      .get('maritalStatus')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((res) => {
        this.updateMarialStatus(res);
      });
  }

  private snilsValidator(control: FormControl) {
    const snils = this.utilService.removeAllButNumbers(control.value);

    const field = this.userFormFieldList.find((item) => item.controlName === 'snils');
    if (field) field.error = '';

    if (snils.length == 11) {
      var checksum = 0;
      for (var i = 0; i < 9; i++) {
        checksum += parseInt(snils.charAt(i)) * (9 - i);
      }
      if (checksum > 101) {
        checksum = checksum % 101;
      }
      if (checksum == 100 || checksum == 101) {
        checksum = 0;
      }

      if (checksum == parseInt(snils.substr(9))) return null;
    }

    if (snils.length === 11 && field) field.error = 'Проверьте правильность ввода';
    return { error: 'Validate error' };
  }

  private innValidator(control: FormControl) {
    const inn = this.utilService.removeAllButNumbers(control.value).split('');

    const field = this.additionalDataFormFieldList.employer.find((item) => item.controlName === 'employersInn');
    if (field) field.error = '';

    if (!inn.length) {
      return null;
    }

    //для ИНН в 10 знаков
    if (
      inn.length == 10 &&
      +inn[9] ==
        ((2 * +inn[0] +
          4 * +inn[1] +
          10 * +inn[2] +
          3 * +inn[3] +
          5 * +inn[4] +
          9 * +inn[5] +
          4 * +inn[6] +
          6 * +inn[7] +
          8 * +inn[8]) %
          11) %
          10
    ) {
      return null;
    }

    //для ИНН в 12 знаков
    if (
      inn.length == 12 &&
      +inn[10] ==
        ((7 * +inn[0] +
          2 * +inn[1] +
          4 * +inn[2] +
          10 * +inn[3] +
          3 * +inn[4] +
          5 * +inn[5] +
          9 * +inn[6] +
          4 * +inn[7] +
          6 * +inn[8] +
          8 * +inn[9]) %
          11) %
          10 &&
      +inn[11] ==
        ((3 * +inn[0] +
          7 * +inn[1] +
          2 * +inn[2] +
          4 * +inn[3] +
          10 * +inn[4] +
          3 * +inn[5] +
          5 * +inn[6] +
          9 * +inn[7] +
          4 * +inn[8] +
          6 * +inn[9] +
          8 * +inn[10]) %
          11) %
          10
    ) {
      return null;
    }

    if (inn.length >= 10 && field) field.error = 'Проверьте правильность ввода';
    return { incorrect: true };
  }

  private addressValidator(control: FormControl) {
    const field = this.additionalDataFormFieldList.address.find((item) => item.controlName === 'actualAddress');
    field.error = '';

    const value = control.value;
    if (!value) {
      field.error = '';
      return { incorrect: true };
    }
    const re = /\s?[а-яё0-9\\/-]*\s?[0-9]+[а-я]*$/;
    const valueToLowerCase = value.toLowerCase();
    if (!re.test(valueToLowerCase)) {
      field.error = 'Введите корректный адрес';
      return { incorrect: true };
    }
    return null;
  }

  private checkPrevDock(doc: { prevDocser: string; prevDocnum: string; prevDocorgancode: string }) {
    let neadValidate: boolean = Boolean(doc.prevDocser) || Boolean(doc.prevDocnum) || Boolean(doc.prevDocorgancode);
    this.updatePersonalFormsValidator('prevDock', 'prevDocser', neadValidate, [Validators.minLength(4)]);
    this.updatePersonalFormsValidator('prevDock', 'prevDocnum', neadValidate, [Validators.minLength(6)]);
    this.updatePersonalFormsValidator('prevDock', 'prevDocorgancode', neadValidate, [Validators.minLength(6)]);
  }

  private updateMarialStatus(status: string) {
    const showSpousesIncome = status === 'Женат/замужем';

    this.additionalDataFormFieldList.marital.find((c) => c.controlName === 'spousesIncome').isHidden =
      !showSpousesIncome;
    this.additionalDataFormFieldList.marital.find((c) => c.controlName === 'numberOfDependents').fakeDsbl =
      status === null;
    this.updateMaritalFormFields.next(this.additionalDataFormFieldList.marital);

    this.updatePersonalFormsValidator('marital', 'spousesIncome', showSpousesIncome);
  }

  private updatePersonalFormsValidator(form: string, control: string, validate: boolean, additionalValidators?: any[]) {
    const formGroup = this.additionalDataFormGroup.get(form) as FormGroup;

    const groupControl = formGroup.get(control);
    if (validate) {
      if (groupControl.validator) return;
      const validators = [Validators.required].concat(additionalValidators || []);
      groupControl.setValidators(validators);
    } else {
      if (!groupControl.validator) return;
      groupControl.clearValidators();
    }
    groupControl.markAsTouched();
    groupControl.updateValueAndValidity();
  }

  private markAsTouchedForms(formName: string, withNested?: boolean) {
    const formGroup = this[formName] as FormGroup;

    Object.keys(formGroup.controls).forEach((key) => {
      if (!withNested) {
        formGroup.get(key).markAsTouched();
      } else {
        const nestedFormGroup = formGroup.get(key) as FormGroup;
        Object.keys(nestedFormGroup.controls).forEach((nestedKey) => {
          nestedFormGroup.get(nestedKey).markAsTouched();
        });
      }
    });
  }

  // private setRegAddressToActualAddress() {
  //   const regAddress = this.userFormGroup.get('regAddress').value;
  //   const actualAddressControl = this.getActualAddressControl();
  //   this.addressService.searchAddres(regAddress)
  //     .subscribe((res) => {
  //       const address = res.length >= 1 ? res[0].value : '';
  //       actualAddressControl.setValue(address, {emitEvent: false});
  //       if (address) {
  //         this.additionalDataFormGroup.get('address.actualAddressUuid').setValue(res[0].data.fias_id);
  //         this.additionalDataFormGroup.get('address.kladrCod').setValue(res[0].data.region_kladr_id);
  //       }
  //       this.cdr.detectChanges();
  //     })
  // }

  private listenAddressNotEqualChanges() {
    const actualAddressControl = this.getActualAddressControl();
    const actualAddressField = this.additionalDataFormFieldList.address.find(
      (field) => field.controlName === 'actualAddress'
    );
    const setActualAddress = (address) => {
      actualAddressControl.setValue(address);
      this.fillAddressKladrAndUuid(address);
    };
    this.additionalDataFormGroup
      .get('address.addressNotEqual')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        actualAddressField.fakeDsbl = !value;
        value ? actualAddressControl.enable() : actualAddressControl.disable();
        value ? actualAddressControl.setValue('') : setActualAddress(this.personalFormService.actualAddress);
      });
  }

  private getActualAddressControl() {
    return this.additionalDataFormGroup.get('address.actualAddress');
  }

  goBack() {
    this.stepManagerService.setStepLoaded(false);
    void this.navigationService.push('/new-product/credit/loan-calculation', { replaceUrl: true });
    // this.stepManagerService.changeStep(Steps.calculation);
  }

  abort() {
    this.stepManagerService.showAbortAlert();
  }

  fillAddressKladrAndUuid(address: string) {
    this.addressService.searchAddress(address).subscribe((res) => {
      const foundedAddress = this.findAddress(address, res);
      if (foundedAddress) {
        this.additionalDataFormGroup.get('address.actualAddressUuid').setValue(res[0].data.fias_id);
        this.additionalDataFormGroup.get('address.kladrCod').setValue(res[0].data.region_kladr_id);
      }
    });
  }

  private findAddress(address: string, addresses: DaDataAddress[]) {
    let v;
    if (addresses.length > 1) {
      v = addresses.find((a) => a.value === address);
    }
    if (addresses.length === 1) {
      v = addresses[0];
    }

    return v;
  }
}
